import type {
  AllocationBlockCause,
  AllocationReadiness,
  AllocationRunStatus,
  BlockedCustomerCause,
  CreditOutcome,
  CustomerLotStage,
  CustomerOrderStage,
  JoorWaitingReason,
  OrderConfirmationRunStatus,
  OrderConfirmationStatus,
  SoLineStatus,
  SoLotStatus,
  SoStatus,
} from "@nexxt/contracts/sales";
import type { Tone } from "./tone";

/**
 * Tone of the states of a sales order (spec v0.2 §5.2, design-system.md §2.3): `confirmed` waits for the allocation
 * and `awaiting_payment` for Credit Control, a human step in F1; the steps the system takes on the way are `info`.
 */
export const soStatusTone: Record<SoStatus, Tone> = {
  confirmed: "warning",
  credit_blocked: "danger",
  allocated: "info",
  locked: "info",
  awaiting_payment: "warning",
  document_requested: "info",
  document_error: "danger",
  document_issued: "info",
  released_to_3pl: "info",
  completed: "success",
  cancelled: "neutral",
};

/**
 * The process of a lot (ADR-195) as the one of an order: the same tone for the same step; shipped is done. A lot that
 * left and waits for the invoice of its units shipped (ADR-197) is a step the system takes.
 */
export const soLotStatusTone: Record<SoLotStatus, Tone> = {
  locked: soStatusTone.locked,
  awaiting_payment: soStatusTone.awaiting_payment,
  document_requested: soStatusTone.document_requested,
  document_error: soStatusTone.document_error,
  document_issued: soStatusTone.document_issued,
  released_to_3pl: soStatusTone.released_to_3pl,
  awaiting_invoice: "info",
  shipped: "success",
  cancelled: "neutral",
};

export const soLineStatusTone: Record<SoLineStatus, Tone> = {
  open: "info",
  allocated: "info",
  partially_fulfilled: "info",
  fulfilled: "success",
  closed: "neutral",
  cancelled: "neutral",
};

/**
 * The steps of an order as its customer reads them in Frontrow (ADR-219): on the way until it is over; nothing of
 * Tomorrow's is a warning to the customer.
 */
export const customerOrderStageTone: Record<CustomerOrderStage, Tone> = {
  confirmed: "info",
  allocated: "info",
  in_preparation: "info",
  shipping: "info",
  completed: "success",
  cancelled: "neutral",
};

/** A shipment of the order for its customer: its payment is the one step that waits for the customer. */
export const customerLotStageTone: Record<CustomerLotStage, Tone> = {
  in_preparation: "info",
  awaiting_payment: "warning",
  at_warehouse: "info",
  shipped: "success",
  cancelled: "neutral",
};

/** The credit of an order: a block stops it, an approval or an override lets it go on (B-18). */
export const creditOutcomeTone: Record<CreditOutcome, Tone> = {
  pending: "neutral",
  approved: "success",
  blocked: "danger",
  override_approved: "success",
};

/**
 * Why an order of Joor waits (ADR-111, ADR-124): something Nexxt lacks needs a correction; a change of an order in
 * progress, or of one cancelled in Nexxt, waits for the decision of the Customer Service.
 */
export const joorWaitingTone: Record<JoorWaitingReason, Tone> = {
  unknown_brand: "danger",
  unknown_customer: "danger",
  missing_legal_entity: "danger",
  missing_mapping: "danger",
  unknown_item: "danger",
  totals_mismatch: "danger",
  order_in_progress: "warning",
  order_cancelled: "warning",
  purchase_reduced: "warning",
};

/** A run of the allocation (ADR-113): one that failed kept what it offered, but the warehouse waits for another. */
export const allocationRunStatusTone: Record<AllocationRunStatus, Tone> = {
  queued: "info",
  running: "info",
  completed: "success",
  failed: "danger",
  cancelled: "neutral",
};

/**
 * The order confirmations of a brand and a season (ADR-198): a run that gave up failed what it had not issued, and the
 * Customer Service asks again.
 */
export const orderConfirmationRunStatusTone: Record<OrderConfirmationRunStatus, Tone> = {
  running: "info",
  completed: "success",
  aborted: "danger",
};

/** A confirmation of an order: issued with its PDF and sent, or failed with why. */
export const orderConfirmationStatusTone: Record<OrderConfirmationStatus, Tone> = {
  requested: "info",
  issued: "success",
  failed: "danger",
};

/** Rule 4 of the allocation (spec v0.2 §6): what an order booked against what it needs to be ready. */
export const allocationReadinessTone: Record<AllocationReadiness, Tone> = {
  green: "success",
  yellow: "warning",
  red: "danger",
};

/** Why a run offered a line less (FR-M7-07): a credit block stops the order, the others are about the stock. */
export const allocationBlockCauseTone: Record<AllocationBlockCause, Tone> = {
  credit: "danger",
  no_availability: "warning",
  outranked: "warning",
};

/** Why a customer is blocked (spec v0.2 §9, P8.10a): the blacklist is the hardest stop, the rest waits for a decision. */
export const blockedCustomerCauseTone: Record<BlockedCustomerCause, Tone> = {
  on_hold: "warning",
  blacklisted: "danger",
  orders_blocked: "warning",
};
